import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    markLinkedInAsDone,
    markFirstEmailAsDone,
    markDMAsDone,
    markFollowupEmailAsDone,
    markEmailSentLocally,
    fetchLinkedinDoneContacts,
    fetchFirstEmailDoneContacts,
    fetchDMDoneContacts,
    fetchFollowupEmailDoneContacts,
    fetchOutreachStatus,
    fetchStageProgress,
    saveStageProgress,
} from '../../../redux/DailyTasksSlice/dailyTaskSlice';
import {
    sendConnectionRequest,
    checkInviteStatusesBatch,
    sendFirstLiMessage,
    isUsableProviderId,
} from '../../../redux/LIAuthSlice/liAuthSlice';

import {
    ArrowLeft, Check, ExternalLink, MessageSquare, Mail, FileCheck,
    Send, MapPin,
} from 'lucide-react';

import SendDmModal from './SendDMModal';
import EmailGenerationModal from './EmailGenerationModal';
import { CompanyLogoTile, isUsablePhoto } from './NetPipelineRow';

/* Networking · sequence flow — full page that opens when a company is clicked.
   Horizontal 5-stage flow (done = green check, active = filled indigo, todo =
   muted, connected by progress lines), then the active stage expands below with
   contacts in a 2-column grid and Back / Skip / Complete navigation.
   All stage actions (invite / email / DM / mark-done) preserve the original
   NetCompanyProfile behavior. */

const AVATAR_GRADIENTS = [
    ['#3F57E0', '#6377F0'], ['#7C3AED', '#A78BFA'], ['#059669', '#34D399'],
    ['#DC2626', '#F87171'], ['#B45309', '#FCD34D'], ['#0369A1', '#38BDF8'],
];
function initials(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(' ').filter(Boolean);
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
}
function gradientFor(name) {
    if (!name) return AVATAR_GRADIENTS[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
}
function Avatar({ contact, size = 34 }) {
    const [failed, setFailed] = React.useState(false);
    const photo = contact?.profile_picture || contact?.photo_url || contact?.image;
    const hasPhoto = isUsablePhoto(photo) && !failed;
    const [g1, g2] = gradientFor(contact?.name);
    if (hasPhoto) {
        return <img src={photo} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} onError={() => setFailed(true)} />;
    }
    return (
        <div className="rounded-full grid place-items-center text-white font-bold flex-shrink-0 select-none"
            style={{ width: size, height: size, fontSize: size * 0.34, background: `linear-gradient(135deg, ${g1}, ${g2})` }}>
            {initials(contact?.name)}
        </div>
    );
}

// Stage metadata — app's real 5-stage sequence, design's flow visuals.
const STAGE_META = {
    1: { title: 'Email recruiters', Icon: Mail },
    2: { title: 'Connect on LinkedIn', Icon: ExternalLink },
    3: { title: 'Send a personalized DM', Icon: MessageSquare },
    4: { title: 'Follow up by email', Icon: Mail },
    5: { title: 'Tailor resume & apply', Icon: FileCheck },
};

const normalizeLinkedinProfileUrl = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return '';
    return s.split('?')[0].replace(/\/$/, '').replace(/\/recent-activity\/all$/i, '');
};

// Full, openable LinkedIn profile URL for a contact (or '' if none is known).
const contactLinkedinUrl = (contact) => {
    const url = normalizeLinkedinProfileUrl(contact?.linkedin_url || contact?.profile_url);
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, '')}`;
};

const NetSequenceFlow = ({ companyData, onBack }) => {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth);
    const isGoogleConnected = useSelector((state) => state.auth.isGoogleConnected);
    const { linkedinDone, firstEmailDone, dmDone, followupEmailDone, sentFirstEmails, sentFollowupEmails, outreachByContact, stageProgressByCompany, stageProgressLoaded } = useSelector((state) => state.dailyTasks);
    const { inviteLoadingByContact, inviteStatusesByProviderId, inviteStatusesByContactId, isLinkedInConnected } = useSelector((state) => state.liauth);

    // DB key for this company's stage progress — must match the pipeline list's
    // company key (org id, falling back to company name).
    const companyKey = String(companyData?.organizationId || companyData?.name || 'company');

    // Per-company localStorage keys so manually-completed stages survive a page refresh.
    const _storageBase = `${user?._id || user?.id || 'anon'}_${companyKey}`;
    const stagesStorageKey = `cc_net_stages_${_storageBase}`;
    const viewStorageKey = `cc_net_viewstage_${_storageBase}`;

    const [viewStage, setViewStage] = useState(() => {
        try { const raw = localStorage.getItem(viewStorageKey); return raw ? Number(raw) : null; } catch { return null; }
    });
    const [dmModalOpen, setDmModalOpen] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailModalStep, setEmailModalStep] = useState(1);
    const [emailModalMode, setEmailModalMode] = useState('email'); // 'email' | 'message'
    const [selectedContact, setSelectedContact] = useState(null);
    const [completedStages, setCompletedStages] = useState(() => {
        try { const raw = localStorage.getItem(stagesStorageKey); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch { return new Set(); }
    });
    const [successModalOpen, setSuccessModalOpen] = useState(false);

    // Persist completed stages + current stage so they survive a refresh.
    useEffect(() => {
        try { localStorage.setItem(stagesStorageKey, JSON.stringify(Array.from(completedStages))); } catch { /* ignore */ }
    }, [completedStages, stagesStorageKey]);

    // Hydrate stage progress from the DB (source of truth across devices), and
    // migrate any stages that so far exist only in localStorage up to the DB.
    // Read under the company key AND the plain name (older rows were saved by
    // name before the flow knew the org id); always save under the company key.
    useEffect(() => {
        if (!stageProgressLoaded) { dispatch(fetchStageProgress()); return; }
        const canonical = stageProgressByCompany[companyKey] || [];
        const dbStages = [
            ...canonical,
            ...(stageProgressByCompany[String(companyData?.name || '')] || []),
        ];
        setCompletedStages((prev) => {
            const merged = new Set([...prev, ...dbStages.map(Number)]);
            if (merged.size > canonical.length) {
                dispatch(saveStageProgress({ companyKey, completedStages: Array.from(merged) }));
            }
            return merged.size === prev.size ? prev : merged;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stageProgressLoaded, stageProgressByCompany, companyKey, dispatch]);
    useEffect(() => {
        try { if (viewStage != null) localStorage.setItem(viewStorageKey, String(viewStage)); } catch { /* ignore */ }
    }, [viewStage, viewStorageKey]);

    const authUserId = user?._id || user?.id;
    const allContacts = useMemo(() => companyData?.allContacts || [], [companyData]);

    // Hydrate persisted outreach status (which contacts had a first/follow-up
    // email sent + the prior email content) so Stage 4 gating, "Sent" badges, and
    // follow-up context survive reloads.
    useEffect(() => {
        const ids = allContacts.map((c) => c?.contactid || c?._id).filter(Boolean);
        if (ids.length) dispatch(fetchOutreachStatus(ids));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allContacts]);

    // Source (job / hiring signal / both) + saved state badges for the header.
    const SOURCE_BADGES = {
        job:    { label: 'Job',          bg: '#EEF3FF', color: '#3F57E0' },
        signal: { label: 'Hiring Signal', bg: '#FFF7ED', color: '#D97706' },
        both:   { label: 'Job + Signal',  bg: '#F0FDF4', color: '#059669' },
    };
    const sourceBadge = SOURCE_BADGES[companyData?.source] || null;
    const isSaved = Boolean(companyData?.savedJob || companyData?.savedSignal);

    useEffect(() => {
        if (authUserId) {
            dispatch(fetchLinkedinDoneContacts(authUserId));
            dispatch(fetchFirstEmailDoneContacts(authUserId));
            dispatch(fetchDMDoneContacts(authUserId));
            dispatch(fetchFollowupEmailDoneContacts(authUserId));
        }
    }, [dispatch, authUserId]);

    // A contact counts as a recruiter for the email stage if the backend tagged
    // it as one (predicted_role/contactType) OR its title looks recruiter-ish.
    // Covers "Talent Acquisition", "HR", "People", "Sourcer", etc. — not just the
    // literal word "recruiter".
    const recruiterContacts = useMemo(
        () => allContacts.filter((c) => {
            const role = String(c.predicted_role || c.contactType || '').toLowerCase();
            if (role === 'recruiter') return true;
            const title = String(c.title || '').toLowerCase();
            return /recruit|talent|\bhr\b|human resources|\bpeople\b|sourc/.test(title);
        }),
        [allContacts]
    );
    // Email stages target every contact that has an email address (not just
    // recruiter-titled ones) so the user can reach all available people.
    const contactsWithEmail = useMemo(
        () => allContacts.filter((c) => c.email && String(c.email).trim() !== ''),
        [allContacts]
    );
    // Connected (1st-degree) contacts — detect via contactId OR providerId, matching the
    // "Connected" badge logic in Stage 2 (a contactId match alone is enough).
    const acceptedContacts = useMemo(
        () => allContacts.filter((c) => {
            const cid = c?._id != null ? String(c._id) : '';
            const pid = c?.providerId != null ? String(c.providerId) : '';
            const providerStatus = isUsableProviderId(pid) ? inviteStatusesByProviderId[pid] : undefined;
            const contactStatus = cid ? (inviteStatusesByContactId[cid] || inviteStatusesByContactId[c?.contactid]) : undefined;
            return (contactStatus || providerStatus) === 'accepted';
        }),
        [allContacts, inviteStatusesByProviderId, inviteStatusesByContactId]
    );

    const stepContacts = useMemo(() => ({
        1: contactsWithEmail,
        2: (allContacts || []).slice(0, 10),
        3: acceptedContacts,
        4: contactsWithEmail,
        5: [],
    }), [allContacts, contactsWithEmail, acceptedContacts]);

    const stepDescriptions = {
        1: 'Send an email to the recruiters.',
        2: 'Send connection invites to these contacts on LinkedIn.',
        3: "Send personalized direct messages to the contacts you've engaged with.",
        4: 'Send a follow-up email to recruiters who have not yet responded.',
        5: 'Tailor your resume and apply for the position.',
    };

    const doneList = {
        1: firstEmailDone, 2: linkedinDone, 3: dmDone, 4: followupEmailDone, 5: [],
    };

    const isStepCompleted = (step) => {
        const contacts = stepContacts[step] || [];
        const done = doneList[step] || [];
        if (contacts.length === 0) return false;
        const doneSet = new Set((done || []).map((id) => String(id)));
        return contacts.every((c) => doneSet.has(String(c?._id)));
    };

    // A stage counts as done if every contact is marked done OR the user explicitly
    // hit "Complete & go to Stage N" (tracked in completedStages).
    const isStageDone = (step) => completedStages.has(step) || isStepCompleted(step);

    // First stage that isn't done yet — counts both per-contact completion and
    // manual "Complete & go to Stage N" completions, so reopening the flow lands
    // on the stage the user actually left off at.
    const activeStage = useMemo(() => {
        for (let s = 1; s <= 5; s++) if (!isStageDone(s)) return s;
        return 5;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedinDone, firstEmailDone, dmDone, followupEmailDone, allContacts, inviteStatusesByProviderId, completedStages]);

    // Stages are sequential: a stage is reachable only if it's already done or
    // it's the first not-done one. Everything past that is locked.
    const isStageReachable = (step) => step <= activeStage || isStageDone(step);

    // Default the visible stage to the first incomplete one, and clamp any
    // restored view that points at a now-locked stage. Wait for the DB progress
    // fetch so we don't clamp to stage 1 before completions have hydrated.
    useEffect(() => {
        if (!stageProgressLoaded) return;
        setViewStage((prev) => {
            if (prev == null) return activeStage;
            return isStageReachable(prev) ? prev : activeStage;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStage, stageProgressLoaded]);

    const stagesDoneCount = useMemo(() => {
        let n = 0;
        for (let s = 1; s <= 5; s++) if (isStageDone(s)) n++;
        return n;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedinDone, firstEmailDone, dmDone, followupEmailDone, allContacts, inviteStatusesByProviderId, completedStages]);

    // Batch-check invite statuses + auto-mark accepted invites as LinkedIn-done.
    useEffect(() => {
        if (isLinkedInConnected?.accountId && allContacts.length > 0) {
            const providerIds = [];
            allContacts.forEach((c) => {
                const pid = c.providerId;
                if (isUsableProviderId(pid) && inviteStatusesByProviderId[pid] === undefined) providerIds.push(pid);
            });
            if (providerIds.length > 0) dispatch(checkInviteStatusesBatch({ accountId: isLinkedInConnected.accountId, providerIds }));
        }
    }, [dispatch, isLinkedInConnected?.accountId, allContacts, inviteStatusesByProviderId]);

    useEffect(() => {
        if (!authUserId || !allContacts.length) return;
        allContacts.forEach((c) => {
            const status = inviteStatusesByProviderId[c.providerId];
            const isDone = linkedinDone.includes(c._id) || linkedinDone.includes(String(c._id));
            if (status === 'accepted' && !isDone) dispatch(markLinkedInAsDone({ userId: authUserId, contactId: c._id }));
        });
    }, [inviteStatusesByProviderId, allContacts, linkedinDone, dispatch, authUserId]);

    const markDone = (step, contactId) => {
        if (!authUserId) return;
        const payload = { userId: authUserId, contactId };
        if (step === 1) dispatch(markFirstEmailAsDone(payload));
        else if (step === 2) dispatch(markLinkedInAsDone(payload));
        else if (step === 3) dispatch(markDMAsDone(payload));
        else if (step === 4) dispatch(markFollowupEmailAsDone(payload));
    };

    const handleSendInvite = (profileUrl, contactId, providerId) => {
        const normalized = normalizeLinkedinProfileUrl(profileUrl);
        const accountId = isLinkedInConnected?.accountId;
        if (accountId && contactId && (normalized || providerId)) {
            dispatch(sendConnectionRequest({ profileUrl: normalized, accountId, contactId, providerId }));
        }
    };

    const handleSendMessage = async (accountId, attendeeId, text, userId) => {
        if (accountId && attendeeId && text && userId) {
            await dispatch(sendFirstLiMessage({ accountId, attendeeId, text, userId }));
        }
    };

    const tierOf = (contact) => {
        const preferred = companyData?.preferredContacts || [];
        if (recruiterContacts.some((rc) => rc._id === contact._id)) return 1;
        if (preferred.some((pc) => pc._id === contact._id)) return 2;
        return 3;
    };

    const STAGE_STYLE = {
        done: { c: '#047857', bg: '#ECFDF5', ring: '#A7F3D0' },
        active: { c: '#3F57E0', bg: '#EEF1FE', ring: '#C7D0F8' },
        todo: { c: '#94A3B8', bg: '#F8FAFC', ring: '#E2E8F0' },
    };
    const stageStateOf = (step) => {
        if (isStageDone(step)) return 'done';
        if (step === activeStage) return 'active';
        return 'todo';
    };

    const stage = viewStage || activeStage;
    const contactsForStage = stepContacts[stage] || [];
    const doneForStage = new Set((doneList[stage] || []).map((id) => String(id)));
    const doneCountForStage = contactsForStage.filter((c) => doneForStage.has(String(c._id))).length;
    const stageCompleted = completedStages.has(stage) || isStepCompleted(stage);
    const ActiveIcon = STAGE_META[stage].Icon;

    /* ── Per-contact action button (stage-aware) ── */
    const renderContactAction = (contact) => {
        const hasEmail = !!(contact.email && String(contact.email).trim() !== '');

        // Stage 3 — these contacts are already connected; draft + send a LinkedIn DM.
        if (stage === 3) {
            const isDmDone = dmDone.includes(String(contact._id)) || dmDone.includes(contact.contactid);
            return (
                <button
                    onClick={() => {
                        setSelectedContact({ ...contact, accountId: isLinkedInConnected?.accountId, userId: authUserId });
                        setEmailModalMode('message');
                        setEmailModalStep(3);
                        setEmailModalOpen(true);
                    }}
                    disabled={isDmDone}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold flex-shrink-0 transition-colors ${
                        isDmDone ? 'border border-emerald-300 bg-emerald-50 text-emerald-700 cursor-not-allowed'
                            : 'bg-[#3F57E0] text-white hover:bg-[#3347C8]'
                    }`}
                >
                    <MessageSquare style={{ width: 13, height: 13 }} />
                    {isDmDone ? 'Message sent' : 'Generate personalized message'}
                </button>
            );
        }

        if (stage === 2) {
            const profileUrl = normalizeLinkedinProfileUrl(contact.linkedin_url);
            const isSending = inviteLoadingByContact[contact._id] || false;
            const cid = contact?._id != null ? String(contact._id) : '';
            const pid = contact?.providerId != null ? String(contact.providerId) : '';
            const providerStatus = isUsableProviderId(pid) ? inviteStatusesByProviderId[pid] : undefined;
            const contactStatus = cid ? (inviteStatusesByContactId[cid] || inviteStatusesByContactId[contact?._id]) : undefined;
            const status = contactStatus || providerStatus;

            let text = 'Send Invite';
            let cls = 'bg-[#3F57E0] text-white hover:bg-[#3347C8]';
            let disabled = false;
            if (isSending) { text = 'Sending…'; cls = 'bg-gray-200 text-gray-500 cursor-wait'; disabled = true; }
            else if (status === 'pending') { text = 'Pending'; cls = 'border border-amber-300 bg-amber-50 text-amber-600 cursor-not-allowed'; disabled = true; }
            else if (status === 'accepted') { text = 'Connected'; cls = 'border border-emerald-300 bg-emerald-50 text-emerald-700 cursor-not-allowed'; disabled = true; }
            else if (status === 'rejected') { text = 'Declined'; cls = 'border border-red-200 bg-red-50 text-red-500 cursor-not-allowed'; disabled = true; }
            else if (status === 'error' || status === 'failed') { text = 'Retry'; cls = 'bg-red-600 text-white hover:bg-red-700'; }
            else if (!isLinkedInConnected?.accountId || (!contact.providerId && !contact.linkedin_url)) { text = 'N/A'; cls = 'bg-gray-100 text-gray-400 cursor-not-allowed'; disabled = true; }

            return (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                        onClick={() => { if (!disabled) handleSendInvite(profileUrl, contact._id, contact.providerId); }}
                        disabled={disabled}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold transition-colors ${cls}`}
                    >
                        {!disabled && status !== 'accepted' && <Send style={{ width: 12, height: 12 }} />}
                        {text}
                    </button>
                </div>
            );
        }

        if ((stage === 1 || stage === 4) && hasEmail) {
            const cid = contact.contactid || contact._id;
            const firstSent = sentFirstEmails.includes(cid) || !!outreachByContact?.[cid]?.first?.sent;
            // Stage 4 is a follow-up to the Stage 1 email — it stays locked for a
            // contact until that contact's first email has actually been sent.
            const followupLocked = stage === 4 && !firstSent;
            const openModal = () => { setSelectedContact(contact); setEmailModalMode("email"); setEmailModalStep(stage); setEmailModalOpen(true); };

            if (isGoogleConnected) {
                const hasBeenSent = stage === 1 ? firstSent : (sentFollowupEmails.includes(cid) || !!outreachByContact?.[cid]?.followup?.sent);
                const label = hasBeenSent ? 'Sent'
                    : followupLocked ? 'Send the first email first'
                    : (stage === 4 ? 'Generate personalized followup email' : 'Generate personalized email');
                return (
                    <button
                        onClick={openModal}
                        disabled={hasBeenSent || followupLocked}
                        title={followupLocked ? 'Send the Stage 1 email to this contact before following up' : undefined}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold flex-shrink-0 transition-colors ${
                            hasBeenSent ? 'border border-emerald-300 bg-emerald-50 text-emerald-700 cursor-not-allowed'
                                : followupLocked ? 'border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                                : 'bg-[#3F57E0] text-white hover:bg-[#3347C8]'
                        }`}
                    >
                        <Mail style={{ width: 13, height: 13 }} />
                        {label}
                    </button>
                );
            }
            return (
                <button
                    onClick={openModal}
                    disabled={followupLocked}
                    title={followupLocked ? 'Send the Stage 1 email to this contact before following up' : undefined}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold flex-shrink-0 transition-colors ${
                        followupLocked ? 'border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-[#3F57E0] text-white hover:bg-[#3347C8]'
                    }`}
                >
                    <Mail style={{ width: 13, height: 13 }} /> {followupLocked ? 'Send the first email first' : (stage === 4 ? 'Generate personalized followup email' : 'Generate personalized email')}
                </button>
            );
        }

        return null;
    };

    return (
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-[18px]">
            {/* Company header */}
            <div className="bg-white rounded-2xl border border-[#E6E8EE] px-6 py-5 flex items-center gap-4" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
                <div className="w-[52px] h-[52px] rounded-[13px] bg-white border border-[#EEF1F5] grid place-items-center flex-shrink-0 overflow-hidden">
                    <CompanyLogoTile src={companyData?.companyLogo} name={companyData?.name} size={40} />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="text-[18px] font-extrabold text-[#0F172A] tracking-tight truncate text-left">{companyData?.name || 'Company'}</div>
                        {sourceBadge && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: sourceBadge.bg, color: sourceBadge.color }}>{sourceBadge.label}</span>
                        )}
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={isSaved ? { background: '#ECFDF5', color: '#059669' } : { background: '#F1F3F7', color: '#64748B' }}>
                            {isSaved ? 'Saved' : 'Unsaved'}
                        </span>
                    </div>
                    <div className="text-[13.5px] font-semibold text-[#3F57E0] truncate mt-0.5 text-left">
                        {[companyData?.jobPosition, companyData?.hqLocation].filter(Boolean).join(' · ') || `${allContacts.length} contacts`}
                    </div>
                </div>

                {/* Meta (right-aligned, left of stages): Headquarters + Contacts */}
                <div className="flex-1 flex items-center justify-end gap-10">
                    <div className="text-left">
                        <div className="text-[11px] text-[#94A3B8] font-semibold">Headquarters</div>
                        <div className="text-[13px] text-[#0F172A] font-medium mt-0.5 inline-flex items-center gap-1">
                            <MapPin style={{ width: 12, height: 12 }} /> {companyData?.hqLocation || '—'}
                        </div>
                    </div>
                    <div className="text-left">
                        <div className="text-[11px] text-[#94A3B8] font-semibold">Contacts</div>
                        <div className="text-[13px] text-[#0F172A] font-medium mt-0.5">{allContacts.length} {allContacts.length === 1 ? 'person' : 'people'}</div>
                    </div>
                </div>

                <div className="text-center px-5 border-l border-[#EEF1F5]">
                    <div className="text-[22px] font-extrabold text-[#3F57E0] leading-none">{stagesDoneCount}/5</div>
                    <div className="text-[11px] text-[#64748B] mt-1">Stages done</div>
                </div>
            </div>

            {/* Horizontal stage flow */}
            <div className="bg-white rounded-2xl border border-[#E6E8EE] px-6 py-5" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#94A3B8] mb-4">Outreach sequence</div>
                <div className="flex items-start overflow-x-auto pb-1">
                    {[1, 2, 3, 4, 5].map((step, i) => {
                        const st = stageStateOf(step);
                        const m = STAGE_STYLE[st];
                        const { Icon } = STAGE_META[step];
                        const isViewing = step === stage;
                        const locked = !isStageReachable(step);
                        return (
                            <React.Fragment key={step}>
                                <button
                                    onClick={() => { if (!locked) setViewStage(step); }}
                                    disabled={locked}
                                    title={locked ? `Complete Stage ${activeStage} first` : undefined}
                                    className={`flex flex-col items-center text-center flex-shrink-0 focus:outline-none ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                                    style={{ width: 132 }}
                                >
                                    <div
                                        className="grid place-items-center transition-all"
                                        style={{
                                            width: 48, height: 48, borderRadius: 9999,
                                            background: st === 'active' ? '#3F57E0' : m.bg,
                                            color: st === 'active' ? '#fff' : m.c,
                                            border: st === 'active' ? 'none' : `2px solid ${m.ring}`,
                                            boxShadow: st === 'active' ? '0 6px 16px rgba(63,87,224,.35)' : 'none',
                                            outline: isViewing && st !== 'active' ? `2px solid ${m.c}` : 'none',
                                            outlineOffset: 2,
                                        }}
                                    >
                                        {st === 'done' ? <Check style={{ width: 20, height: 20 }} /> : <Icon style={{ width: 18, height: 18 }} />}
                                    </div>
                                    <div className="text-[11px] font-bold uppercase tracking-wider mt-2" style={{ color: m.c }}>Stage {step}</div>
                                    <div className="text-[12.5px] font-semibold mt-0.5 leading-snug px-1" style={{ color: st === 'todo' ? '#94A3B8' : '#0F172A' }}>
                                        {STAGE_META[step].title}
                                    </div>
                                </button>
                                {i < 4 && (
                                    <div className="flex-1 rounded-full mt-[23px]" style={{ height: 2, minWidth: 24, background: isStageDone(step) ? '#10B981' : '#E6E8EE' }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Active stage detail */}
            <div className="bg-white rounded-2xl border border-[#E6E8EE]" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
                <div className="px-6 py-4 flex items-center gap-3 border-b border-[#EEF1F5] rounded-t-2xl" style={{ background: 'linear-gradient(135deg, #EEF1FE 0%, #fff 70%)' }}>
                    <div className="w-[38px] h-[38px] rounded-[10px] bg-[#3F57E0] text-white grid place-items-center flex-shrink-0">
                        <ActiveIcon style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[15px] font-extrabold text-[#0F172A]">Stage {stage} · {STAGE_META[stage].title}</div>
                        <div className="text-[12px] text-[#64748B] mt-0.5">{stepDescriptions[stage]}</div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold flex-shrink-0 ${stageCompleted ? 'bg-emerald-50 text-emerald-700' : 'bg-[#EEF1FE] text-[#3F57E0]'}`}>
                        {contactsForStage.length > 0 ? `${doneCountForStage}/${contactsForStage.length}` : (stage === 5 ? 'Final step' : '0/0')}
                    </span>
                </div>

                {/* Contacts grid */}
                {stage === 5 ? (
                    <div className="px-6 py-10 text-center">
                        <div className="w-12 h-12 rounded-2xl bg-[#EEF1FE] grid place-items-center mx-auto mb-3">
                            <FileCheck style={{ width: 24, height: 24, color: '#3F57E0' }} />
                        </div>
                        <p className="text-[14px] font-bold text-[#0F172A]">Finish strong — tailor your resume & apply</p>
                        <p className="text-[12.5px] text-[#64748B] mt-1">You've warmed up your network. Tailor your resume to this role and apply with a referral.</p>
                    </div>
                ) : contactsForStage.length === 0 ? (
                    <div className="px-6 py-10 text-center text-[12.5px] text-[#94A3B8]">
                        No contacts available for this stage yet.
                    </div>
                ) : (
                    <div className="px-6 py-5 grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {contactsForStage.map((contact) => {
                            const tier = tierOf(contact);
                            const isDone = doneForStage.has(String(contact._id));
                            const tierStyle = tier === 1 ? 'text-[#3F57E0] bg-[#EEF1FE]' : tier === 2 ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50';
                            return (
                                <div key={contact._id} className="px-4 py-3.5 rounded-xl border border-[#EEF1F5] bg-white flex items-center gap-3">
                                    <span className={`text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 ${tierStyle}`}>Tier {tier}</span>
                                    {(() => {
                                        const liUrl = contactLinkedinUrl(contact);
                                        const openProfile = () => { if (liUrl) window.open(liUrl, '_blank', 'noopener,noreferrer'); };
                                        return (
                                            <div
                                                role={liUrl ? 'link' : undefined}
                                                title={liUrl ? 'View LinkedIn profile' : undefined}
                                                onClick={openProfile}
                                                className={`flex items-center gap-3 flex-1 min-w-0 ${liUrl ? 'cursor-pointer group' : ''}`}
                                            >
                                                <Avatar contact={contact} size={34} />
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-[13px] font-bold text-[#0F172A] truncate text-left ${liUrl ? 'group-hover:text-[#3F57E0] group-hover:underline' : ''}`}>{contact.name}</div>
                                                    <div className="text-[11.5px] text-[#64748B] truncate text-left">{contact.title}</div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {renderContactAction(contact)}
                                    <button
                                        onClick={() => markDone(stage, contact._id)}
                                        disabled={isDone}
                                        title={isDone ? 'Done' : 'Mark done'}
                                        className={`w-8 h-8 grid place-items-center rounded-lg flex-shrink-0 transition-colors ${
                                            isDone ? 'bg-emerald-50 text-emerald-600 cursor-default' : 'border border-[#E6E8EE] text-[#94A3B8] hover:bg-gray-50 hover:text-[#3F57E0]'
                                        }`}
                                    >
                                        <Check style={{ width: 15, height: 15 }} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Footer nav — pinned to the bottom of the scroll area so it's always reachable.
                    Extra right padding keeps the Complete button clear of the floating chat widget. */}
                <div className="sticky bottom-0 z-20 px-6 py-3.5 border-t border-[#EEF1F5] bg-white rounded-b-2xl flex items-center gap-2.5">
                    <button
                        onClick={() => setViewStage(Math.max(1, stage - 1))}
                        disabled={stage <= 1}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#E6E8EE] bg-white text-[12.5px] font-semibold text-[#334155] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ArrowLeft style={{ width: 13, height: 13 }} /> Stage {Math.max(1, stage - 1)}
                    </button>
                    <span className="flex-1" />
                    <button
                        onClick={() => {
                            const next = new Set(completedStages).add(stage);
                            setCompletedStages(next);
                            // Persist to the DB so the pipeline list reflects it and
                            // progress survives refreshes / other devices.
                            dispatch(saveStageProgress({ companyKey, completedStages: Array.from(next) }));
                            if (stage < 5) setViewStage(stage + 1);
                            else setSuccessModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#3F57E0] text-white text-[12.5px] font-semibold hover:bg-[#3347C8] transition-colors"
                    >
                        {stage < 5 ? <>Complete &amp; go to Stage {stage + 1}</> : 'Mark complete'}
                    </button>
                </div>
            </div>

            {/* Sequence-complete success modal */}
            {successModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setSuccessModalOpen(false)}>
                    <div
                        className="bg-white rounded-2xl w-[92%] max-w-[420px] px-8 py-9 text-center"
                        style={{ boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 grid place-items-center mx-auto">
                            <Check style={{ width: 30, height: 30, color: '#059669' }} />
                        </div>
                        <div className="text-[19px] font-extrabold text-[#0F172A] mt-4">Outreach sequence complete! 🎉</div>
                        <p className="text-[13px] text-[#64748B] mt-2 leading-relaxed">
                            You've finished all 5 stages for <span className="font-bold text-[#0F172A]">{companyData?.name || 'this company'}</span>
                            {companyData?.jobPosition ? <> · <span className="font-semibold">{companyData.jobPosition}</span></> : null}.
                            Your network is warmed up — go get that referral!
                        </p>
                        <div className="flex items-center justify-center gap-2.5 mt-6">
                            <button
                                onClick={() => setSuccessModalOpen(false)}
                                className="px-4 py-2 rounded-xl border border-[#E6E8EE] bg-white text-[12.5px] font-semibold text-[#334155] hover:bg-gray-50"
                            >
                                Stay here
                            </button>
                            <button
                                onClick={() => { setSuccessModalOpen(false); if (onBack) onBack(); }}
                                className="px-4 py-2 rounded-xl bg-[#3F57E0] text-white text-[12.5px] font-semibold hover:bg-[#3347C8]"
                            >
                                Back to pipeline
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SendDmModal
                isOpen={dmModalOpen}
                onClose={() => { setDmModalOpen(false); setSelectedContact(null); }}
                contactName={selectedContact?.name}
                companyName={selectedContact?.organization_name}
                selectedContact={selectedContact}
                handleSendMessage={handleSendMessage}
            />
            <EmailGenerationModal
                isOpen={emailModalOpen}
                onClose={() => { setEmailModalOpen(false); setSelectedContact(null); setEmailModalMode('email'); }}
                contact={selectedContact}
                jobData={companyData}
                companyData={companyData}
                outreachMode={emailModalMode}
                step={emailModalStep}
                emailType={emailModalStep === 4 ? 'followup' : 'first'}
                previousEmail={emailModalStep === 4 && selectedContact
                    ? outreachByContact?.[selectedContact.contactid || selectedContact._id]?.first || null
                    : null}
                // Non-ObjectId taskId → message goes straight to start-chat (no /tasks lookup);
                // inviteStatus 'accepted' since Stage 3 only lists connected contacts.
                taskId={emailModalMode === 'message' && selectedContact ? `net-${selectedContact._id || selectedContact.contactid || 'c'}` : undefined}
                inviteStatus={emailModalMode === 'message' ? 'accepted' : undefined}
                onSuccess={(meta) => {
                    if (emailModalMode === 'message') {
                        if ((meta?.automated || meta?.sent) && authUserId && selectedContact?._id) {
                            dispatch(markDMAsDone({ userId: authUserId, contactId: selectedContact._id }));
                        }
                    } else if (meta?.sent && (selectedContact?.contactid || selectedContact?._id) != null) {
                        dispatch(markEmailSentLocally({ contactId: selectedContact.contactid || selectedContact._id, step: emailModalStep, subject: meta.subject, body: meta.body }));
                    }
                }}
            />
        </div>
    );
};

export default NetSequenceFlow;
